<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  summary: { type: Object, required: true },
});

const { t } = useI18n();

const scoreCriteria = computed(() =>
  props.summary.criteria.filter(criterion => criterion.kind === 'score')
);
const booleanCriteria = computed(() =>
  props.summary.criteria.filter(criterion => criterion.kind === 'boolean')
);
const enumCriteria = computed(() =>
  props.summary.criteria.filter(criterion => criterion.kind === 'enum')
);

const maxBand = computed(() => {
  const counts = Object.values(props.summary.score_distribution || {});
  return counts.length ? Math.max(...counts) : 0;
});

const bandHeight = count =>
  maxBand.value ? `${Math.max(4, (count * 100) / maxBand.value)}%` : '4%';
</script>

<template>
  <div class="grid gap-3 md:grid-cols-3">
    <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
      <p class="text-sm text-n-slate-11">
        {{ t('ANALYSIS.QUALITY.SUMMARY.TOTAL') }}
      </p>
      <p class="text-2xl font-semibold text-n-slate-12 tabular-nums">
        {{ summary.total }}
      </p>
    </div>

    <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
      <p class="text-sm text-n-slate-11">
        {{ t('ANALYSIS.QUALITY.SUMMARY.AVERAGE') }}
      </p>
      <p
        v-if="summary.average_score !== null"
        class="text-2xl font-semibold text-n-slate-12 tabular-nums"
      >
        {{ summary.average_score }}
      </p>
      <p v-else class="text-sm text-n-slate-11 pt-2">
        {{ t('ANALYSIS.QUALITY.SUMMARY.NO_SCORE') }}
      </p>
    </div>

    <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
      <p class="text-sm text-n-slate-11">
        {{ t('ANALYSIS.QUALITY.SUMMARY.DISTRIBUTION') }}
      </p>
      <div class="flex items-end gap-1.5 h-12 mt-2">
        <div
          v-for="(count, band) in summary.score_distribution"
          :key="band"
          class="flex flex-col items-center flex-1 gap-1"
          :title="`${band}: ${count}`"
        >
          <div class="w-full flex items-end h-9">
            <div
              class="w-full rounded-sm bg-n-brand"
              :style="{ height: bandHeight(count) }"
            />
          </div>
          <span class="text-[10px] text-n-slate-11 tabular-nums">{{
            band
          }}</span>
        </div>
      </div>
    </div>

    <div
      v-for="criterion in scoreCriteria"
      :key="criterion.key"
      class="p-4 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <p class="text-sm text-n-slate-11 truncate">{{ criterion.name }}</p>
      <p class="text-2xl font-semibold text-n-slate-12 tabular-nums">
        {{ criterion.average }}
      </p>
      <p class="text-xs text-n-slate-11 tabular-nums">
        {{ criterion.answered }}
      </p>
    </div>

    <div
      v-for="criterion in booleanCriteria"
      :key="criterion.key"
      class="p-4 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <p class="text-sm text-n-slate-11 truncate">{{ criterion.name }}</p>
      <p class="text-2xl font-semibold text-n-slate-12 tabular-nums">
        {{ `${criterion.rate}%` }}
      </p>
      <p class="text-xs text-n-slate-11 tabular-nums">
        {{ `${criterion.true_count} / ${criterion.answered}` }}
      </p>
    </div>

    <div
      v-for="criterion in enumCriteria"
      :key="criterion.key"
      class="p-4 border rounded-xl border-n-weak bg-n-solid-1 md:col-span-2"
    >
      <p class="text-sm text-n-slate-11">{{ criterion.name }}</p>
      <div class="flex flex-col gap-1.5 mt-2">
        <div
          v-for="option in criterion.options.slice(0, 6)"
          :key="option.option"
          class="flex items-center gap-2"
        >
          <span class="text-sm text-n-slate-12 w-32 truncate">
            {{ option.option }}
          </span>
          <div class="flex-1 h-2 rounded-full bg-n-alpha-2 overflow-hidden">
            <div
              class="h-full rounded-full bg-n-brand"
              :style="{
                width: `${(option.count * 100) / criterion.answered}%`,
              }"
            />
          </div>
          <span class="text-sm text-n-slate-11 tabular-nums w-10 text-right">
            {{ option.count }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
